import { Button, Drawer, message } from "antd";
import React, { useEffect } from "react";
import Swal from "sweetalert2";
import useCheckDemoPath from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useIntercomWithData from "../../hooks/useIntercomWithData";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { createClient, deleteClient, updateClient, validateClientName } from "../../service/client.service";
import { setCreateClientState, setSelectedClient } from "../../slices/appStateSlice";
import { addClient, removeClient, resetClientForm, updateClientInList } from "../../slices/clientSlice";
import ClientForm from "./ClientForm.component";

const CreateClient = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const checkDemoPath = useCheckDemoPath();
  const { referIndividualBy } = useSpecialityReference();
  const { bootWithData, shutdown } = useIntercomWithData();

  const [submissionInProgres, setSubmissionInProgres] = React.useState(false);
  const { isCreateClientDrawerVisible, selectedClient, isMobilePath } = useAppSelector((state) => state.appState);
  const { clientForm, isEditing } = useAppSelector((state) => state.client);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const closeDrawer = () => {
    dispatch(setCreateClientState(false));
    dispatch(resetClientForm());
  };

  useEffect(() => {
    shutdown();

    return () => {
      if (!userInfo.is_paying || userInfo.group_name) {
        bootWithData();
      }
    };
  }, [shutdown, bootWithData, userInfo.is_paying, userInfo.group_name]);

  const submitClient = async () => {
    if (checkDemoPath()) {
      return;
    }
    setSubmissionInProgres(true);

    const clientNameValidationResult = validateClientName(clientForm, referIndividualBy);

    if (clientNameValidationResult) {
      message.warning(clientNameValidationResult);
      setSubmissionInProgres(false);
      return;
    }

    createClient({
      ...clientForm,
      name: clientForm.name.trim(),
      diagnosis_ids: clientForm.diagnosis_ids
        ? clientForm.diagnosis_ids?.map((item) => Number.parseInt(item.toString()))
        : [],
      // Only send age if DOB is not set (backwards compatibility)
      age: clientForm.date_of_birth ? undefined : clientForm.age ? Number(clientForm.age) : undefined,
    })
      .then((response) => {
        message.success(`${referIndividualBy} added successfully`);
        dispatch(setCreateClientState(false));
        dispatch(resetClientForm());
        dispatch(addClient(response.data));
      })
      .catch((error) => {
        console.error(error);
        message.error(`Failed to add ${referIndividualBy.toLowerCase()}`);
      })
      .finally(() => {
        setSubmissionInProgres(false);
      });
  };

  const deletePatient = () => {
    if (checkDemoPath()) {
      return;
    }
    if (clientForm.id) {
      Swal.fire({
        title: "Are you sure?",
        text: `You will not be able to recover this ${referIndividualBy.toLowerCase()}.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, delete ${referIndividualBy.toLowerCase()}!`,
        cancelButtonText: `No, keep ${referIndividualBy.toLowerCase()}`,
      }).then((result) => {
        if (result.isConfirmed && clientForm && clientForm.id) {
          deleteClient(clientForm.id)
            .then(() => {
              message.success(`${referIndividualBy} deleted successfully`);
              dispatch(setCreateClientState(false));
              dispatch(resetClientForm());
              if (selectedClient && clientForm.id === selectedClient.id) {
                dispatch(setSelectedClient(null));
              }
              dispatch(removeClient(clientForm.id as number));
            })
            .catch((error) => {
              console.error(error);
              message.error(`Failed to delete ${referIndividualBy.toLowerCase()}`);
            });
        }
      });
    }
  };

  const updatePatient = () => {
    if (checkDemoPath()) {
      return;
    }
    setSubmissionInProgres(true);

    const clientNameValidationResult = validateClientName(clientForm, referIndividualBy);

    if (clientNameValidationResult) {
      message.warning(clientNameValidationResult);
      setSubmissionInProgres(false);
      return;
    }

    if (!clientForm.id) {
      setSubmissionInProgres(false);
      return;
    }
    updateClient(clientForm.id, {
      ...clientForm,
      name: clientForm.name.trim(),
      diagnosis_ids: clientForm.diagnosis_ids
        ? clientForm.diagnosis_ids?.map((item) => Number.parseInt(item.toString()))
        : [],
      // Only send age if DOB is not set (backwards compatibility)
      age: clientForm.date_of_birth ? undefined : clientForm.age ? Number(clientForm.age) : undefined,
    })
      .then((response) => {
        message.success(`${referIndividualBy} updated successfully`);
        dispatch(setCreateClientState(false));
        dispatch(resetClientForm());
        dispatch(updateClientInList(response.data));
        dispatch(setSelectedClient(response.data));
      })
      .catch((error) => {
        console.error(error);
        message.error(`Failed to update ${referIndividualBy.toLowerCase()}`);
      })
      .finally(() => {
        setSubmissionInProgres(false);
      });
  };

  return (
    <Drawer
      open={isCreateClientDrawerVisible}
      title={isEditing ? `Edit ${referIndividualBy}` : `Add ${referIndividualBy}`}
      onClose={closeDrawer}
      width={700}
      destroyOnClose
      height={isMobile && !isMobilePath ? "80%" : "100%"}
      placement={isMobile ? "bottom" : "right"}
      footer={
        isEditing
          ? [
              <Button
                key="back"
                type="default"
                onClick={deletePatient}
                style={{
                  marginRight: 8,
                }}
              >
                {`Delete ${referIndividualBy}`}
              </Button>,
              <Button
                disabled={submissionInProgres}
                loading={submissionInProgres}
                key="back"
                type="primary"
                onClick={updatePatient}
              >
                {`Update ${referIndividualBy}`}
              </Button>,
            ]
          : [
              <Button
                key="back"
                disabled={submissionInProgres}
                loading={submissionInProgres}
                type="primary"
                onClick={submitClient}
              >
                {`Add ${referIndividualBy}`}
              </Button>,
            ]
      }
    >
      <ClientForm />
    </Drawer>
  );
};

export default CreateClient;
